/*
 * The site-wide delegated click framework (#165, GA4-E5-00).
 *
 * WHY THIS EXISTS, AND WHY IT IS URGENT. The `data-gtm-*` attribute convention shipped first and the
 * listener did not. Nine files already carry those attributes — #161, #175, #177, #178, and the
 * store and franchise instrumentation — and until this file loaded, EVERY ONE OF THEM WAS INERT.
 * The attributes were being rendered to every visitor and read by nothing. This is the half that
 * makes them do something, which is why every other ticket in E5 is blocked behind it.
 *
 * ONE listener, on `document`, in the capture phase. Not one per element and not one per section:
 * adding tracking to a new control is then an attribute in Liquid, not a change to this file, not a
 * new GTM trigger, and not a re-bind after AJAX. Filters, infinite scroll, the cart drawer and quick
 * view all inject DOM after load; delegation covers them by construction.
 *
 * CAPTURE PHASE, deliberately. A bubbling listener never runs if something inner calls
 * stopPropagation() — and this theme has plenty of inner click handlers. Capture runs on the way
 * down, before any of them, so a measured click cannot be swallowed by a handler that had nothing to
 * do with measurement. It also means we record the click that was made, not the click that survived.
 *
 * ONE EVENT NAME PER ACTION, MANY LABELS. The event name comes off the element rather than being
 * invented here, and #165 step 4 asks for a single GTM tag reading it from a dataLayer variable, so
 * a new kind of click needs no new tag. #163 and #164 are open because this property already fires
 * events more often than the thing they describe happened — nothing here may add to that, which is
 * what the de-duplication and the cap below are for.
 *
 * NO PII, EVER. Enforced upstream: `analytics-click-attrs` says labels are literals, written and not
 * interpolated. This file adds nothing of its own to the payload beyond the page context, so it
 * cannot introduce a value the caller did not choose.
 *
 * CONSENT. Click labels are literals describing our own UI, so they carry no visitor data and follow
 * the same rule `page_data` already follows in `analytics-datalayer.liquid`: not gated. The gate
 * there gates identifiers, and there are none here. If #185/#186 later decide that click volume
 * itself is gated, `shouldSend()` is the one place to change.
 */
(function () {
  'use strict';

  // #165 step 7. A runaway loop — a handler that synthesises clicks, a stuck carousel — would
  // otherwise spend the property's event quota silently. The cap is per page load, and it says so
  // once in the console rather than failing quietly, because a report that is short by an unknown
  // amount is worse than one that is short by a stated amount.
  var MAX_EVENTS_PER_PAGE = 100;
  var sent = 0;
  var capAnnounced = false;

  function dataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  // page_type and template come from #141's push rather than being recomputed here — two sources for
  // one fact is how they end up disagreeing. Read the most recent one: a SPA-ish navigation could
  // push a second.
  function pageContext() {
    var dl = dataLayer();
    for (var i = dl.length - 1; i >= 0; i--) {
      var entry = dl[i];
      if (entry && entry.page_data) {
        return {
          page_type: entry.page_data.page_type || null,
          template_name: entry.page_data.template_name || null
        };
      }
    }
    return { page_type: null, template_name: null };
  }

  // `data-gtm-meta` is author-supplied JSON. A malformed blob must lose the blob, not the event —
  // the click still happened and the label is still the useful part.
  function parseMeta(raw) {
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function buildPayload(el, ctx) {
    var event = el.getAttribute('data-gtm-click');
    if (!event) return null;

    var payload = {
      event: event,
      click_category: el.getAttribute('data-gtm-category') || null,
      click_label: el.getAttribute('data-gtm-label') || null,
      page_type: ctx.page_type,
      template_name: ctx.template_name
    };

    // Always present, `undefined` when the element has none. GTM's data layer MERGES pushes, so a key
    // left out keeps the previous click's value: after a tile click, a header phone click went to GA4
    // with the tile's `click_position` and `click_destination` (#705). `undefined` clears the stored
    // value and the GA4 tag then omits the parameter.
    var position = el.getAttribute('data-gtm-position');
    payload.click_position = position || undefined;

    var destination = el.getAttribute('data-gtm-destination');
    payload.click_destination = destination || undefined;

    var meta = parseMeta(el.getAttribute('data-gtm-meta'));
    payload.click_meta = meta || undefined;

    return payload;
  }

  function shouldSend() {
    if (sent >= MAX_EVENTS_PER_PAGE) {
      if (!capAnnounced) {
        capAnnounced = true;
        // eslint-disable-next-line no-console
        console.warn('[om-analytics] click cap of ' + MAX_EVENTS_PER_PAGE + ' reached; further clicks on this page are not measured.');
      }
      return false;
    }
    return true;
  }

  function nearestTracked(node) {
    // `closest` is missing on some nodes a click can originate from (older SVG implementations), so
    // fall back to walking up. Returning null is fine: an untracked click is the normal case.
    if (!node) return null;
    if (typeof node.closest === 'function') return node.closest('[data-gtm-click]');
    var current = node;
    while (current && current.nodeType === 1) {
      if (current.hasAttribute && current.hasAttribute('data-gtm-click')) return current;
      current = current.parentNode;
    }
    return null;
  }

  // #176. Social, share and app links live in admin-entered HTML and in app-injected markup, so no
  // Liquid attribute can ever reach them — instrumenting only what the theme renders would measure
  // the footer links and miss every link that actually leaves the site. A generic rule catches all of
  // them, wherever they are authored, and keeps working when marketing edits the footer HTML.
  //
  // Deliberately NOT outbound: `mailto:` and `tel:`. Those are support contacts, already measured as
  // `contact_click` by #175, and counting them twice is the #163/#164 mistake again.
  function outboundPayload(node, ctx) {
    var a = null;
    var cur = node;
    while (cur && cur.nodeType === 1) {
      if (cur.tagName === 'A') { a = cur; break; }
      cur = cur.parentNode;
    }
    if (!a) return null;

    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return null;
    if (/^(mailto:|tel:|javascript:)/i.test(href)) return null;

    // Relative hrefs resolve to our own host, so `a.hostname` answers this without parsing.
    var host = a.hostname;
    if (!host || host === window.location.hostname) return null;

    return {
      event: 'outbound_click',
      click_category: 'outbound',
      click_label: host,
      click_destination: a.href,
      click_position: undefined,  // cleared, not inherited from an earlier click (#705)
      click_meta: undefined,
      page_type: ctx.page_type,
      template_name: ctx.template_name
    };
  }

  function onClick(e) {
    var target = e && (e.target || e.srcElement);
    if (!target) return;

    var el = nearestTracked(target.nodeType === 1 ? target : target.parentNode);

    // An attributed element wins. If a link is both tracked and outbound — a footer social link that
    // someone later gives a label — it is measured once, as the thing its author named it.
    if (!el) {
      if (!shouldSend()) return;
      var outbound = outboundPayload(target.nodeType === 1 ? target : target.parentNode, pageContext());
      if (!outbound) return;
      sent++;
      dataLayer().push(outbound);
      return;
    }

    // Promo slots are measured by analytics-promotions.js as select_promotion. That file's own note
    // says they must not also carry data-gtm-click or the click counts twice. Belt and braces: if a
    // future edit adds both, this drops the generic one rather than double-counting — the same class
    // of bug as #163 and #164, and not one worth reintroducing through a different door.
    if (el.hasAttribute('data-gtm-promo-id')) return;

    if (!shouldSend()) return;

    var payload = buildPayload(el, pageContext());
    if (!payload) return;

    sent++;
    dataLayer().push(payload);
  }

  document.addEventListener('click', onClick, true);

  // Exposed only so the payload builder can be exercised without a browser. Nothing in the theme
  // reads this, and nothing should.
  window.__omAnalyticsClicks = { buildPayload: buildPayload, parseMeta: parseMeta };
})();
